import { useRef, type RefObject } from 'react';
import { useGetScreensInfiniteQuery } from './hooks/useGetScreensInfiniteQuery';
import { useGetScreensMetaQuery } from './hooks/useGetScreensMetaQuery';
import type { AtomicRendererRefCurrent } from './types';
import { Flex, Spinner } from '@banhala/ui-kit';

import { useScrollToFilterList } from './hooks/useScrollToFilterList';
import { useLogViewEvent } from './hooks/useLogViewEvent';
import type { SetState } from 'shared/src/types';
import type { ScreensMetaResponse } from 'shared/src/api';
import { useSetTitle } from './hooks/useSetTitle';
import { useSetDropdownNavigator } from './hooks/useSetDropdownNavigator';
import { View, StyleSheet } from 'react-native';
import { AtomicRenderer } from 'shared/src/component';
import { useGetCommonParams } from '../hooks';

type ScreensResultProps = {
  setTitle?: SetState<string | undefined>;
  setDropdownNavigator?: SetState<
    ScreensMetaResponse['screen']['dropdown_navigator'] | null
  >;
  onScrollY?: (scrollY: number) => void;
  onBeginDrag?: (scrollY: number) => void;
  onEndDrag?: (scrollY: number) => void;
  listRef?: RefObject<AtomicRendererRefCurrent>;
};

export const ScreensResult = ({
  setTitle,
  setDropdownNavigator,
  onScrollY,
  onBeginDrag,
  onEndDrag,
  listRef: externalListRef,
}: ScreensResultProps) => {
  const internalListRef = useRef<AtomicRendererRefCurrent>(null);
  const atomicRendererRef = externalListRef ?? internalListRef;
  const { nextToken } = useGetCommonParams();

  const { data, fetchNextPage, isFetching, isLoading, refetch } =
    useGetScreensInfiniteQuery(nextToken);
  const { data: metaData } = useGetScreensMetaQuery();
  useLogViewEvent({ logging: data?.logging, screenName: data?.screenName });
  const { filterListIndex, scrollToFilterList } = useScrollToFilterList({
    atomicRendererRef,
    components: data?.components,
  });
  useSetTitle({ setTitle, title: data?.title });
  useSetDropdownNavigator({
    setDropdownNavigator,
    dropdownNavigator: metaData?.screen.dropdown_navigator,
  });

  if (isLoading) {
    return (
      <Flex.Center flex={1}>
        <Spinner size={20} />
      </Flex.Center>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <Flex flex={1}>
      {isFetching && (
        <View style={styles.spinnerContainer}>
          <Spinner size={20} />
        </View>
      )}
      <AtomicRenderer
        refetch={refetch}
        data={data}
        fetchNextPage={fetchNextPage}
        listRef={atomicRendererRef}
        filterListIndex={filterListIndex}
        scrollToFilterList={scrollToFilterList}
        isFetching={isFetching}
        onScrollY={onScrollY}
        onBeginDrag={onBeginDrag}
        onEndDrag={onEndDrag}
      />
    </Flex>
  );
};

const styles = StyleSheet.create({
  spinnerContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: [{ translateX: -10 }, { translateY: -10 }],
    zIndex: 1000,
  },
});
